import React from 'react';
import clsx from 'clsx';

const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  icon,
  className,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center gap-1.5 font-medium rounded-full transition-all duration-200 whitespace-nowrap';

  const variantStyles = {
    default: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
    primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
    success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
    warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
    error: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    info: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    live: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
    scheduled: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
    completed: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    cancelled: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
    paused: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
    expired: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  };

  const sizeStyles = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5',
  };

  const dotColors = {
    default: 'bg-neutral-400',
    primary: 'bg-primary-500',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    live: 'bg-emerald-500',
    scheduled: 'bg-amber-500',
    completed: 'bg-blue-500',
    cancelled: 'bg-red-500',
    paused: 'bg-neutral-400',
    expired: 'bg-red-500',
  };

  const badgeClasses = clsx(baseStyles, variantStyles[variant], sizeStyles[size], className);
  const isLive = variant === 'live';

  return (
    <span className={badgeClasses} {...props}>
      {dot && (
        <span className={clsx('w-1.5 h-1.5 rounded-full', dotColors[variant], isLive && 'animate-pulse')} />
      )}
      {icon && <span className="inline-flex w-3.5 h-3.5">{icon}</span>}
      {children}
    </span>
  );
};

Badge.Status = ({ status, ...props }) => {
  const statusConfig = {
    online: { variant: 'success', dot: true, children: 'Online' },
    offline: { variant: 'default', dot: true, children: 'Offline' },
    live: { variant: 'live', dot: true, children: 'Live' },
    scheduled: { variant: 'scheduled', children: 'Scheduled' },
    completed: { variant: 'completed', children: 'Completed' },
    cancelled: { variant: 'cancelled', children: 'Cancelled' },
    paused: { variant: 'paused', children: 'Paused' },
    expired: { variant: 'expired', children: 'Expired' },
  };

  const config = statusConfig[status] || statusConfig.offline;
  return <Badge {...config} {...props} />;
};

Badge.Count = ({ count, max = 99, className, ...props }) => {
  const displayCount = count > max ? `${max}+` : count;

  return (
    <Badge
      variant="error"
      size="sm"
      className={clsx('min-w-[1.25rem] justify-center !bg-red-500 !text-white', className)}
      {...props}
    >
      {displayCount}
    </Badge>
  );
};

export default Badge;
